import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloudOff, RotateCw } from "lucide-react";
import { TopBar, DestinationRow, NumberTile, ActionButton, SecondaryAction } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import { MatchRow } from "./MatchRow";
import { DeleteMatchDialog } from "./DeleteMatchDialog";
import { withCreate } from "../lib/activityCreate";
import { withEditMatch } from "../lib/matchEditLink";
import { counterText, NOT_RECORDED } from "../lib/counters";
// Review round 3, F4 — `toRateText` (D102) moved into `recordFigures.js`,
// the one place this decision lives; this file no longer carries its own
// copy.
import { avgText, battingAverage, economy, heroLine, matchInningsFigures, numberToWords, toRateText } from "../lib/recordFigures";
import { leftOutNote, unreadableOversCount } from "../lib/overs";
import RecordLoadFailedCard from "./RecordLoadFailedCard";
import RecordOverdueCards from "./RecordOverdueCards";
import api from "../lib/api";

/**
 * Issue #761 — "the record after one match", wave 3 slice 2. Behind
 * `newUi("record")`, replaces `/dashboard` for a signed-in player whose
 * summary says `matches === 1` and no filter is active
 * (`pages/Dashboard.jsx`'s `showRecordOne` gate, `lib/recordGate.js`).
 * Matches `OneInnings.dc.html` (round 5, sha256 4d81a43d…) — the approved
 * drawing is the contract; the issue text is ruled by it where the two
 * differ (brief-761.md).
 *
 * SAME "the rendered screen replaces the page" mechanism `RecordEmptyScreen.
 * jsx` (#760) built on: its own navy `TopBar` (D26's contrast trigger),
 * its own `DestinationRow` (current="record"), `ChromelessOverrideContext`
 * / `RetireTextSizeContext` mounted on an empty-deps effect — same
 * lifecycle contract that file's own header documents. No toast handling
 * here at all: D109's suppression lives at each toast's OWN source
 * (Onboarding.jsx/Register.jsx etc.), never in a mount effect on a screen
 * that merely happens to be `/dashboard`'s current composition — see
 * `RecordEmptyScreen.jsx`'s own header for why a blanket dismiss here
 * would be wrong (it mounts for every flag-on `/dashboard` visit, not only
 * this one).
 *
 * THE ONE GAP #760 DID NOT HAVE: this screen's hero line and bowling card
 * need the SPECIFIC match's own detail (ground, dismissal, balls faced,
 * overs/runs conceded) — none of which `GET /stats/summary` returns (it
 * has no `overs`/`runs_conceded` total at all, despite the issue text's own
 * claim; brief-761.md ruling 5 says the file wins). That detail comes from
 * `GET /matches` (`pages/Dashboard.jsx`'s existing `matches` state), a
 * SEPARATE fetch from the summary — `match` is `null` while it has not
 * landed (or failed) yet; `recentMatchesFailed` names the failure
 * explicitly (`Dashboard.jsx` chooses between these three states — see
 * that file's own comment on `showRecordOne`).
 *
 * PR #857 review, F2 — `recentMatchesFailed` used to swap out the WHOLE
 * page for the error card, which broke J08d's own rule ("one failure
 * degrades one card, not the page"): everything this screen can answer
 * from `summary` alone (Total Runs, Matches, Highest, Innings, the batting
 * average, the no-trend section, the footnote, the gold button) has
 * nothing to do with the failed `/matches` fetch and must keep rendering.
 * Only the TWO things that genuinely need `match` degrade: the hero's own
 * descriptive line (omitted outright — there is nothing truthful it could
 * say) and the Bowling card, whose card-shaped slot the "recent matches
 * failed" card takes over instead (`OneInnings.dc.html`'s own
 * `recent-matches-failed` commentary block is drawn card-shaped for
 * exactly this reason).
 *
 * `data-testid="dashboard"` is kept on the outer wrapper deliberately,
 * same reasoning as `RecordEmptyScreen.jsx`'s own note: this IS
 * `/dashboard`, and several journeys (01, 05, 08) read `stat-runs`
 * straight off whatever composition is live after logging a first match.
 *
 * Issue #900 — the one-match record gave no way to open the match at all
 * (no edit, no delete, no reflection): `OneInnings.dc.html` (checked at
 * tag `design-baseline-round-5`) draws no such affordance anywhere on the
 * card, so there is nothing to match here — a DEPARTURE, not an omission.
 * `match` (this screen's own single-match fetch, already required for the
 * hero line and bowling card above) is reused as `MatchRow.jsx`'s `m` —
 * the exact component `SeasonRecordScreen.jsx`'s "Every match" list uses,
 * moved into its own file for this issue so both screens read one
 * definition rather than two that can drift. Delete reuses
 * `DeleteMatchDialog.jsx`, same testids, same in-app alert dialog (#1124), no
 * native `confirm()`. "Add a reflection" is not a third affordance of its
 * own: the log flow's own "Not now" skip already promises "you can add
 * one later, from the match" — opening the match via Edit IS that promise
 * kept, the same edit form that offers the reflection prompt on save.
 *
 * Issue #899 (supersedes D148's stopgap) — Edit opens
 * `withEditMatch(match.match_id)` (`matchEditLink.js`), the same one-tap
 * deep link `SeasonRecordScreen.jsx`'s own pencil uses, not the bare
 * `/performance` list D148 accepted as a stopgap when this screen was
 * built. Saving (or cancelling) there returns here with no toast
 * (`PerformanceTracking.jsx`'s own `editingViaDeepLink`).
 */
export default function RecordOneMatchScreen({
  summary,
  match,
  recentMatchesFailed = false,
  onRetryRecentMatches,
  summaryFailed = false,
  onRetrySummary,
  onMatchDeleted,
}) {
  const nav = useNavigate();
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Issue #900 — same shape as `SeasonRecordScreen.jsx`'s own delete state
  // (that file's own header on `confirmDelete`): `deleteTarget` gates the
  // dialog, `deleting` disables the confirm button mid-request, and a
  // failed delete stays named on the dialog (D57), never a toast.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/matches/${deleteTarget.match_id}`);
      setDeleteTarget(null);
      onMatchDeleted?.();
    } catch (e) {
      console.error("RecordOneMatchScreen: failed to delete match", e);
      setDeleteError("That didn't save. The match is still on your record — try again.");
    } finally {
      setDeleting(false);
    }
  };

  // PR #857 review round 2, B1 — Innings and the batting average are
  // derived from the MATCH record's own figures (`matchInningsFigures`),
  // NEVER from `summary.innings`/`summary.not_outs`: `statsCompute.ts`
  // only counts an innings when `balls_faced > 0`, so a player who
  // entered runs without also entering balls faced (or a genuine duck
  // who did enter balls faced) both read as "zero innings" at the API —
  // which put "No innings yet." (or the not-out reason, silently lost)
  // next to a real, non-zero Total Runs, and "Innings 0" where the
  // drawing never shows a zero. This screen already knows `matches === 1`
  // (the gate that put it here), so the one match's own record IS the
  // whole innings count when it shows any batting at all.
  //
  // When `match` has not landed yet (`recentMatchesFailed`), fall back to
  // `summary` entirely — we have no reason to doubt its own totals, and
  // guessing "no innings" on top of an unrelated fetch failure would be a
  // second, worse failure (J08d's rule; PR #857 review round 1, F2).
  const matchFigures = matchInningsFigures(match);
  const hasInnings = match ? matchFigures.hasInnings : true;
  // Issue #1217 — `GET /stats/summary` failed (`OneInnings.dc.html:136-138`:
  // "the figures show a dash, never a nought"). The card says the tiles
  // below show a dash, so EVERY career-total figure does, including the two
  // that would otherwise be derived from `match` (Innings, Batting Avg): a
  // card claiming the totals didn't load above a real-looking average would
  // contradict itself. The hero line and the bowling card come from the
  // separate `/matches` read, which did load, and stay.
  const inningsCount = summaryFailed ? null : match ? matchFigures.innings : summary?.innings;
  const avgFigures = summaryFailed
    ? { value: null, restsOn: null, hypotheticalNotOut: null, reason: null }
    : battingAverage(
        match
          ? { total_runs: matchFigures.totalRuns, innings: matchFigures.innings, not_outs: matchFigures.notOuts }
          : { total_runs: summary?.total_runs, innings: summary?.innings, not_outs: summary?.not_outs },
      );

  const bowling = match?.bowling || {};
  const econFigures = economy({ runs_conceded: bowling.runs_conceded, overs: bowling.overs });

  const line = heroLine({ hasInnings, match, closing: !summaryFailed });

  const oversWords = Number.isInteger(bowling.overs) ? numberToWords(bowling.overs) : String(bowling.overs);
  const runsConcededWords = Number.isInteger(bowling.runs_conceded)
    ? numberToWords(bowling.runs_conceded)
    : String(bowling.runs_conceded);
  // D260: unreadable overs (a legacy 3.7) have no economy; the line says why.
  const oversLeftOut = unreadableOversCount([match]);
  const bowlingRestsOn = oversLeftOut
    ? `Wickets and overs are totals, so they are shown plainly. There is no economy (${leftOutNote(oversLeftOut)}).`
    : `Wickets and overs are totals, so they are shown plainly. The economy rests on ${oversWords}` +
      ` over${bowling.overs === 1 ? "" : "s"} and ${runsConcededWords} run${bowling.runs_conceded === 1 ? "" : "s"},` +
      " and says so. One over is enough — there is no number of overs to get past first.";

  return (
    // `data-testid="record-one"` on the outer element matches
    // `OneInnings.dc.html:61`'s own `data-dc-id="record-one"` on ITS outer
    // container (structure-check's join key) — `dashboard` moves one level
    // in, on a plain wrapping div, so both testids exist at once: this IS
    // `/dashboard` (same reasoning `RecordEmptyScreen.jsx`'s own header
    // gives for keeping that one), and it is also the drawing's own named
    // root element.
    <div data-testid="record-one" style={{ color: "var(--nw-ink)" }}>
    <div data-testid="dashboard">
      <TopBar
        variant="white"
        // PR #887 review, B4 — `topbar-logo` + the home link `Layout.jsx`'s
        // own header carries everywhere else; this screen is player-only,
        // so its home is always `/dashboard`.
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        // D26 (decisions-2026-09-24.md, same ruling brief-760.md records) —
        // every navy-TopBar artboard in waves 2-6 carries the contrast
        // trigger even where the artboard itself (drawn before D26) leaves
        // it off; `hideTextSize` swaps the "Aa" trigger for the `Contrast`
        // glyph, since this screen's own type ramp is fixed px.
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu hideTextSize surface="white" />
            {/* #780 (D140) — the drawn empty account circle; see AccountMenu.jsx's own docstring on the `empty` prop. */}
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="record-topbar"
      />
      <DestinationRow current="record" />

      <div
        className="flex flex-col"
        style={{ gap: "var(--nw-space-stack)", padding: "22px 20px 20px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="nw-eyebrow">Your record</div>
          <div data-focus-after-delete tabIndex={-1} style={{ outline: "none" }} className="nw-title-lg">One match in</div>
        </div>

        {/* Issue #1217 — `OneInnings.dc.html:138`, "state: failed to load",
            where `SeasonRecordScreen.jsx` puts its own copy of the same
            card: directly under the heading, above the tiles it explains. */}
        {summaryFailed && (
          <RecordLoadFailedCard
            title="Your career totals didn't load"
            help="Nothing has been lost — we couldn't reach the server, so the tiles below show a dash instead of a number."
            onRetry={onRetrySummary}
          />
        )}

        {/* #1150 — the PlanMatchAfter card, above the figures (round-10 board). */}
        <RecordOverdueCards />

        {/* The hero — `OneInnings.dc.html:81-90`. Total Runs, Matches,
            Highest Score and Innings all come from `summary` alone and
            render regardless of `recentMatchesFailed` (J08d's rule); the
            descriptive line needs the specific match record and is left
            out entirely when that fetch has failed — there is nothing
            truthful it could say instead. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: 18,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <NumberTile
            variant="hero"
            label="Total Runs"
            value={counterText(hasInnings ? summary?.total_runs : null)}
            data-testid="stat-runs"
          />
          {!recentMatchesFailed && line && <div className="nw-lede">{line}</div>}
          <div style={{ display: "flex", gap: 20, paddingTop: 4 }}>
            <NumberTile variant="row" label="Matches" value={counterText(summary?.matches)} data-testid="stat-matches" />
            <NumberTile
              variant="row"
              label="Highest Score"
              value={counterText(hasInnings ? summary?.highest : null)}
              data-testid="record-one-highest"
            />
            <NumberTile
              variant="row"
              label="Innings"
              value={counterText(hasInnings ? inningsCount : null)}
              data-testid="record-one-innings"
            />
          </div>
        </div>

        {/* Batting Avg — `OneInnings.dc.html:92-99`, brief-761.md ruling 1/2.
            Built from `summary` alone (`battingAverage` already names "No
            innings yet." when `innings` is 0) — unaffected by
            `recentMatchesFailed`. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: 14,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <NumberTile variant="read" label="Batting Avg" value={avgText(avgFigures.value)} data-testid="stat-avg" />
          {/* #1217 — no helper line while the totals are unknown: the reasons
              ("No innings yet.") would state something the page does not know. */}
          {summaryFailed ? null : avgFigures.value != null ? (
            <>
              <div className="nw-prose" style={{ color: "var(--nw-ink-lede)" }} data-testid="record-one-avg-rests-on">
                {avgFigures.restsOn}
              </div>
              {avgFigures.hypotheticalNotOut && <div className="nw-help">{avgFigures.hypotheticalNotOut}</div>}
            </>
          ) : (
            <div className="nw-help">{avgFigures.reason}</div>
          )}
        </div>

        {/* Bowling — `OneInnings.dc.html:101-118`, brief-761.md ruling 3 —
            OR, when the match fetch failed, the "recent matches failed"
            card takes this card's own slot (PR #857 review, F2): the
            bowling figures need the specific match record exactly as the
            hero line does, and this is the one card-shaped place in the
            drawing that can carry that failure without touching anything
            `summary` already answered. */}
        {recentMatchesFailed ? (
          <div
            data-testid="dash-recent-matches-error"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              padding: "20px 16px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
              textAlign: "center",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                background: "var(--nw-line-faint)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloudOff size={20} style={{ color: "var(--nw-alert)" }} aria-hidden="true" />
            </div>
            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--nw-ink)" }}>
              Couldn&#x27;t load your recent matches
            </div>
            <div className="nw-help">Nothing has been lost — we just couldn&#x27;t reach the server.</div>
            <SecondaryAction
              data-testid="dash-recent-matches-error-retry"
              onClick={onRetryRecentMatches}
              style={{
                marginTop: 6,
                minHeight: 40,
                padding: "0 14px",
                border: "1px solid var(--nw-line-strong)",
                borderRadius: "var(--nw-radius-control)",
                color: "var(--nw-navy)",
                fontSize: 13,
                gap: 6,
              }}
            >
              <RotateCw size={14} aria-hidden="true" />
              Try again
            </SecondaryAction>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: 14,
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            <div className="flex items-center justify-between">
              <span className="nw-section">Bowling</span>
              {econFigures.value == null && !oversLeftOut && (
                <span data-testid="record-one-bowling-dash" className="nw-figure" style={{ color: "var(--nw-ink-muted)" }}>
                  {NOT_RECORDED}
                </span>
              )}
            </div>
            {econFigures.value == null && !oversLeftOut ? (
              <>
                <div className="nw-prose" style={{ color: "var(--nw-ink-lede)" }}>
                  {econFigures.reason}
                </div>
                <SecondaryAction
                  variant="link"
                  data-testid="record-one-log-bowling"
                  style={{ fontSize: 13, alignSelf: "flex-start" }}
                  onClick={() => nav("/performance")}
                >
                  Log a bowling spell
                </SecondaryAction>
              </>
            ) : (
              <>
                <div style={{ display: "flex", gap: 20 }}>
                  <NumberTile variant="row" label="Wickets" value={counterText(bowling.wickets)} />
                  <NumberTile variant="row" label="Overs" value={counterText(bowling.overs)} />
                  <NumberTile variant="row" label="Economy" value={toRateText(econFigures.value)} data-testid="stat-economy" />
                </div>
                <div className="nw-help">{bowlingRestsOn}</div>
              </>
            )}
          </div>
        )}

        {/* Runs over time — `OneInnings.dc.html:120-123`: never a chart at
            one data point (#762 brings the chart back at two). Drawn
            DASHED, unlike the solid-border cards above — a deliberate
            distinction the drawing makes, not a placeholder omission.
            Built from `summary` alone — unaffected by `recentMatchesFailed`. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: 14,
            border: "1px dashed var(--nw-line-strong)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <span className="nw-section">Runs over time</span>
          <div className="nw-prose" style={{ color: "var(--nw-ink-muted)" }} data-testid="record-one-no-trend">
            No trend yet. A line through one point is a decoration, not a trend — this comes back when the record has
            something to draw.
          </div>
        </div>

        {/* Footnote — brief-761.md ruling 4: only the first sentence of the
            drawn commentary; the history sentences after it are designer
            commentary, left out. */}
        <div className="nw-foot">The record holds no minimum sample, on either side.</div>

        {/* Issue #900 (departure — see this component's own header): the
            drawing has no way back to the match at all. `match` is `null`
            while the separate `/matches` fetch has not landed or failed
            (`recentMatchesFailed`), same guard `hasInnings`/the hero line
            already use — nothing to edit or delete until it has.
            Review round 3, F4 — no invented heading/help line around it:
            `OneInnings.dc.html` draws neither, and X-001 ("no derived
            adjective, no 'form', no projection") reads as "nothing
            invented" generally, not narrowly about adjectives. `MatchRow`
            is the departure itself (recorded in this file's own header);
            it does not also get a frame the drawing never had. */}
        {match && (
          <div data-testid="record-one-match">
            <MatchRow m={match} onEdit={() => nav(withEditMatch(match.match_id))} onDelete={() => setDeleteTarget(match)} />
          </div>
        )}

        {/* The one gold element (R-003) — opens log step 1 directly, the
            same `withCreate` pattern #760's own gold button uses. Built
            from nothing but a fixed route — unaffected by
            `recentMatchesFailed`, per J08d's rule. */}
        <ActionButton data-testid="dash-log-match" onClick={() => nav(withCreate("/performance"))} style={{ color: "var(--nw-ink)" }}>
          Log your next match
        </ActionButton>
      </div>
    </div>

    {/* Issue #900 — same extracted dialog `SeasonRecordScreen.jsx` uses
        (`DeleteMatchDialog.jsx`'s own header), same testids, the same alert-verb
        confirm (#1124). */}
    <DeleteMatchDialog
      target={deleteTarget}
      deleting={deleting}
      error={deleteError}
      onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
      onConfirm={confirmDelete}
    />
    </div>
  );
}
